import { Alert } from "@mui/material";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";

const COOKIE_NAME = 'browserTimezoneAlertDismissed';

// Helper functions to get and set cookies
function getCookie(name) {
  if (typeof document === 'undefined') return null;
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

function setCookie(name, value, days = 365) {
  if (typeof document === 'undefined') return;
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/`;
}

export default function BrowserTimezoneAlert({ sx = {}, customMessage = null, cookieName = COOKIE_NAME, ...rest }) {
  BrowserTimezoneAlert.propTypes = {
    sx: PropTypes.object,
    customMessage: PropTypes.string,
    cookieName: PropTypes.string,
  };

  // Check cookie on mount to see if alert was previously dismissed
  const [isTzAlertVisible, setIsTzAlertVisible] = useState(() => {
    const dismissed = getCookie(cookieName);
    return dismissed !== 'true';
  });

  useEffect(() => {
    // Update state if cookie changes (though unlikely in this component)
    const dismissed = getCookie(cookieName);
    if (dismissed === 'true' && isTzAlertVisible) {
      setIsTzAlertVisible(false);
    }
  }, [isTzAlertVisible, cookieName]);

  const handleClose = () => {
    setIsTzAlertVisible(false);
    setCookie(cookieName, 'true', 30); // Set cookie to expire in 1 month
  };

  const browserNow = new Date();

  const currentTzAcronym = browserNow
    .toLocaleString("en", { timeZoneName: "short" })
    .split(" ")
    .pop();
  const currentTzVerbose = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const message = customMessage || `All times are displayed in your browser timezone: ${currentTzAcronym} (${currentTzVerbose})`;

  return (
    isTzAlertVisible && (
      <Alert
        square
        severity="info"
        variant="outlined"
        sx={sx}
        onClose={handleClose}
        {...rest}
      >
        {message}
      </Alert>
    )
  );
}
